/**
 * SaveSetDialog — name a new set or pick an existing one to overwrite
 * (overwriting a set in use routes through the fork prompt upstream).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';

import { Button } from '@/components/ui/layout/Button';
import ModalCard from '@/components/ui/layout/ModalCard';
import { Input } from '@/components/ui/form/input';

import type { LibrarySet } from '../api/client/sets-api';
import type { SaveSetInput } from './use-save-set';

export interface SaveSetDialogProps {
  open: boolean;
  kindLabel: 'formula' | 'rule';
  sets: LibrarySet[];
  busy: boolean;
  onSubmit: (input: SaveSetInput) => void;
  onClose: () => void;
}

const SaveSetDialog: FC<SaveSetDialogProps> = ({
  open,
  kindLabel,
  sets,
  busy,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [overwriteId, setOverwriteId] = useState('');
  const overwriting = overwriteId !== '';
  const canSave = overwriting || name.trim().length > 0;

  const handleSubmit = () => {
    if (!canSave || busy) return;
    onSubmit(
      overwriting
        ? { name: '', overwriteId }
        : { name: name.trim(), description: description.trim() || undefined },
    );
  };

  return (
    <ModalCard
      open={open}
      title={`Save ${kindLabel} set`}
      subtitle="Sets are global — any campaign can load them."
      widthClass="max-w-md"
      zClass="z-[150]"
      busy={busy}
      onClose={onClose}
    >
      <div className="space-y-3">
        <label className="block space-y-1 text-xs font-medium">
          Save as
          <select
            value={overwriteId}
            onChange={(e) => setOverwriteId(e.target.value)}
            className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs"
          >
            <option value="">New set</option>
            {sets.map((set) => (
              <option key={set.id} value={set.id}>
                Overwrite “{set.name}”
              </option>
            ))}
          </select>
        </label>
        {!overwriting ? (
          <>
            <label className="block space-y-1 text-xs font-medium">
              Name
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aggressive ROI — 7 day"
                className="mt-1 h-8 text-xs"
                autoFocus
              />
            </label>
            <label className="block space-y-1 text-xs font-medium">
              Description <span className="font-normal text-muted-foreground">(optional)</span>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this set is for"
                className="mt-1 h-8 text-xs"
              />
            </label>
          </>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSave || busy}
          >
            {busy ? 'Saving…' : overwriting ? 'Overwrite set' : 'Save set'}
          </Button>
        </div>
      </div>
    </ModalCard>
  );
};

export default SaveSetDialog;
